import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Waves, Users, Clock, Shield, Droplets, Sun } from 'lucide-react'
import axios from 'axios'

const Pool = () => {
  const [poolSettings, setPoolSettings] = useState(null)
  const [loading, setLoading] = useState(true)

  // Fetch pool settings from API
  useEffect(() => {
    const fetchPoolSettings = async () => {
      try {
        const response = await axios.get('/api/pool-settings')
        setPoolSettings(response.data)
      } catch (error) {
        console.error('Error fetching pool settings:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchPoolSettings()
  }, [])
  // Dynamically include all images that start with 'pool'
  const poolImages = [
    {
      src: '/images/pool_1.jpg',
      alt: 'Lockridge Forest Pool View 1'
    },
    {
      src: '/images/pool_4.jpg',
      alt: 'Lockridge Forest Pool View 2'
    }
  ]
  const poolFeatures = [
    {
      title: 'Junior Olympic Pool',
      description: '6-lane, 25-meter pool with step access at the shallow end for easy entry and exit',
      icon: <Waves className="w-6 h-6" />
    },
    {
      title: 'Diving Area',
      description: 'Separate diving well with diving board for experienced swimmers',
      icon: <Droplets className="w-6 h-6" />
    },
    {
      title: 'Wading Pool',
      description: 'Separate fenced and gated wading pool perfect for babies and toddlers',
      icon: <Users className="w-6 h-6" />
    },
    {
      title: 'Professional Lifeguards on Duty',
      description: poolSettings ?
        `Weekdays: ${poolSettings.weekdayLifeguardHours} | Weekends: ${poolSettings.weekendLifeguardHours}. Hours subject to change. Swim at own risk when lifeguards are not on duty.` :
        'Certified lifeguards on duty during operating hours. Hours subject to change. Swim at own risk when lifeguards are not on duty.',
      icon: <Shield className="w-6 h-6" />
    }
  ]

  const amenities = [
    {
      name: 'Ample Seating',
      description: 'Comfortable chairs, tables, and umbrellas throughout the pool area'
    },
    {
      name: 'Large Grass Area',
      description: 'Spacious lawn areas perfect for sunbathing and relaxation'
    },
    {
      name: 'Covered Structures',
      description: 'Pavilions available for private parties and events (reservable)'
    },
    {
      name: 'Raised Deck',
      description: 'Elevated viewing area for watching tennis or enjoying the scenery'
    },
    {
      name: 'Picnic Areas',
      description: 'Multiple picnic tables and grills for family gatherings'
    },
    {
      name: 'Professional Maintenance',
      description: 'Beautifully maintained grounds with regular insect and mosquito treatment'
    }
  ]

  // Lifeguard schedule from settings or default values
  const lifeguardSchedule = poolSettings ? [
    {
      day: 'Weekends',
      hours: poolSettings.weekendLifeguardHours,
      note: 'Saturday & Sunday',
      type: 'lifeguard'
    },
    {
      day: 'Weekdays',
      hours: poolSettings.weekdayLifeguardHours,
      note: 'Monday - Friday',
      type: 'lifeguard'
    }
  ] : []

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative text-white min-h-[400px] flex items-center"
        style={{backgroundImage: 'url(/images/LFST_13%20-%202025%20Fall%20Festival.jpg)', backgroundSize: 'cover', backgroundPosition: 'center'}}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/80 via-primary-700/75 to-secondary-900/80"></div>
        <div className="container-custom relative z-10 py-10">
          <div className="max-w-4xl mx-auto text-center">
            {poolSettings?.openingDate && (
              <div className="inline-block mb-6">
                <div className="bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 text-sm font-medium">
                  Opening {poolSettings.openingDate}
                </div>
              </div>
            )}
            <h1 className="mb-6 animate-fade-in text-4xl md:text-5xl lg:text-6xl font-bold">
              Junior Olympic Swimming Pool
            </h1>
            <p className="text-xl md:text-2xl leading-relaxed opacity-95 max-w-3xl mx-auto">
              Dive into summer fun at our professionally maintained 6-lane Junior Olympic pool,
              complete with diving area and dedicated wading pool for our youngest members
            </p>
          </div>
        </div>
      </section>

      {/* Pool Hours */}
      <section className="py-10 bg-white">
        <div className="container-custom">
          <div className="text-center mb-6">
            <h2 className="mb-3 text-gray-900 text-3xl md:text-4xl font-bold">Pool Hours</h2>
            <p className="text-base text-gray-600">
              Pool is open for members and guests during these hours
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
            </div>
          ) : poolSettings && (
            <div className="max-w-xs mx-auto">
              <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-lg shadow-md p-5 text-center border-2 border-primary-200">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 text-white mb-3">
                  <Sun className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold mb-1 text-gray-900">Every Day</h3>
                <p className="text-xl font-semibold text-primary-600">{poolSettings.weekdayPoolHours}</p>
              </div>
            </div>
          )}

          <div className="mt-6 text-center">
            <div className="inline-block bg-yellow-50 border border-yellow-200 rounded-lg px-5 py-3">
              <p className="text-sm text-yellow-800">
                <span className="font-semibold">Note:</span> Swim at your own risk when lifeguards are not on duty.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Lifeguard Hours */}
      <section className="py-10 bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-6">
            <h2 className="mb-3 text-gray-900 text-3xl md:text-4xl font-bold">Lifeguard Hours</h2>
            <p className="text-base text-gray-600">
              Certified lifeguards on duty during these hours
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="max-w-lg mx-auto grid grid-cols-2 gap-4">
              {lifeguardSchedule.map((item, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-5 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 text-white mb-3">
                    <Clock className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold mb-1 text-gray-900">{item.day}</h3>
                  <p className="text-xs text-gray-500 mb-2">{item.note}</p>
                  <p className="text-lg font-semibold text-primary-600">{item.hours}</p>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 text-center">
            <div className="inline-block bg-primary-50 border border-primary-200 rounded-lg px-5 py-3">
              <p className="text-sm text-primary-800">
                <span className="font-semibold">Note:</span> Pool hours are subject to weather conditions and may be adjusted for safety.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pool Features */}
      <section className="py-10 bg-white">
        <div className="container-custom">
          <div className="text-center mb-10">
            <h2 className="mb-4 text-gray-900 text-3xl md:text-4xl font-bold">Pool Features</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our facility is designed to accommodate swimmers of all ages and skill levels
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {poolFeatures.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 text-white mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pool Photo Gallery - Horizontal Scroll */}
      <section className="py-10 bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="container-custom">
          <div className="text-center mb-10">
            <h2 className="mb-4 text-gray-900 text-3xl md:text-4xl font-bold">Photo Gallery</h2>
            <p className="text-lg text-gray-600">
              Take a virtual tour of our beautiful pool facilities
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="flex gap-6 justify-center items-center flex-wrap">
              {poolImages.map((image, index) => (
                <div
                  key={index}
                  className="w-full md:w-[500px]"
                >
                  <div className="rounded-xl overflow-hidden shadow-2xl">
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-[350px] object-cover"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Amenities Section */}
      <section className="py-10 bg-white">
        <div className="container-custom">
          <div className="text-center mb-10">
            <h2 className="mb-4 text-gray-900 text-3xl md:text-4xl font-bold">Pool Area Amenities</h2>
            <p className="text-lg text-gray-600">
              Everything you need for a perfect day at the pool
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {amenities.map((amenity, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-4">
                    <Sun className="w-6 h-6 text-primary-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">{amenity.name}</h3>
                    <p className="text-gray-600 text-sm">{amenity.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pool Details Section */}
      <section className="py-10 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="mb-6 text-gray-900 text-3xl md:text-4xl font-bold">Why Choose Our Pool?</h2>
              <p className="text-lg text-gray-600 mb-6">
                Our Junior Olympic pool is the centerpiece of the Lockridge Forest community, offering
                a safe, clean, and enjoyable environment for swimmers of all ages and abilities.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <Shield className="w-6 h-6 text-primary-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Family-Friendly Design</h4>
                    <p className="text-gray-600">
                      Separate areas for competitive swimming, diving, and safe wading for toddlers
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Waves className="w-6 h-6 text-primary-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Professional Standards</h4>
                    <p className="text-gray-600">
                      Maintained to the highest standards with regular water quality testing
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Sun className="w-6 h-6 text-primary-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Beautiful Setting</h4>
                    <p className="text-gray-600">
                      Nestled in a park-like 9-acre setting with mature trees and landscaping
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img
                  src="/images/pool_3.jpeg"
                  alt="Pool view"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg overflow-hidden shadow-lg">
                  <img
                    src="/images/pool_5.jpg"
                    alt="Pool facilities"
                    className="w-full h-48 object-cover"
                  />
                </div>
                <div className="rounded-lg overflow-hidden shadow-lg">
                  <img
                    src="/images/pool_6.jpg"
                    alt="Wading pool"
                    className="w-full h-48 object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-10 bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="mb-6 text-3xl md:text-4xl font-bold">Ready to Make a Splash?</h2>
            <p className="text-xl mb-8 opacity-95">
              Join Lockridge Forest Swim Tennis Club and enjoy unlimited pool access all summer long.
              Opening May 24th, 2025!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/membership" className="btn btn-primary bg-white text-primary-700 hover:bg-gray-100">
                View Membership Options
              </Link>
              <Link to="/facility-reservation" className="btn btn-outline border-white text-white hover:bg-white hover:text-primary-700">
                Reserve a Facility
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Pool
